import React, { useEffect, useState } from 'react';
import { FiSave, FiCheckCircle } from 'react-icons/fi';
import { MODELS, LANGUAGES } from '../transcribeEngine';
import { getSettings, updateSettings, TranscribeSetting } from '../transcribeApi';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';

export default function TranscribeSettingsTab() {
  const [s, setS] = useState<TranscribeSetting | null>(null);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getSettings().then(setS).catch((e) => setError(e?.message || 'Could not load settings'));
  }, []);

  const patch = (p: Partial<TranscribeSetting>) => {
    setS((cur) => (cur ? { ...cur, ...p } : cur));
    setDirty(true); setFormDirty(true); setMsg('');
  };

  const save = async (): Promise<boolean> => {
    if (!s) return true;
    try {
      const saved = await updateSettings({
        engine: s.engine, model_size: s.model_size, language: s.language, task: s.task,
        autosave_to_notes: s.autosave_to_notes, retention_days: s.retention_days,
        local_engine_url: s.local_engine_url,
      });
      setS(saved); setDirty(false); setFormDirty(false); setMsg('Settings saved.');
      return true;
    } catch (e: any) { setError(e?.message || 'Save failed'); return false; }
  };

  useEffect(() => { registerSaveHandler(dirty ? save : null); return () => registerSaveHandler(null); }); // eslint-disable-line

  if (error && !s) return <div className="trx-card"><div className="trx-error">{error}</div></div>;
  if (!s) return <div className="trx-card"><div className="trx-empty">Loading…</div></div>;

  return (
    <div className="trx-card">
      <div className="trx-result-head"><strong>Default preferences</strong></div>
      <p className="trx-hint" style={{ marginTop: 0 }}>
        These become the starting values in the Transcribe workbench. They’re per-account and stored on the server.
      </p>

      <div className="trx-controls">
        <div className="trx-field">
          <label>Default model</label>
          <select value={s.model_size} onChange={(e) => patch({ model_size: e.target.value as TranscribeSetting['model_size'] })}>
            {MODELS.map((m) => <option key={m.size} value={m.size}>{m.label} · ~{m.approxMB}MB</option>)}
          </select>
        </div>
        <div className="trx-field">
          <label>Default language</label>
          <select value={s.language} onChange={(e) => patch({ language: e.target.value })}>
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div className="trx-field">
          <label>Default output</label>
          <select value={s.task} onChange={(e) => patch({ task: e.target.value as TranscribeSetting['task'] })}>
            <option value="transcribe">Same language (verbatim)</option>
            <option value="translate">Translate → English</option>
          </select>
        </div>
        <div className="trx-field">
          <label>Keep saved transcripts for</label>
          <select value={s.retention_days} onChange={(e) => patch({ retention_days: Number(e.target.value) })}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <span className="trx-hint">Transcripts in Notes auto-delete after this. Videos are never stored.</span>
        </div>
        <div className="trx-field">
          <label>On finish</label>
          <label className="trx-check">
            <input type="checkbox" checked={s.autosave_to_notes === 1}
              onChange={(e) => patch({ autosave_to_notes: e.target.checked ? 1 : 0 })} />
            Auto-save every transcript to Notes
          </label>
        </div>
      </div>

      <div className="trx-actions">
        <button className="trx-btn" onClick={() => void save()} disabled={!dirty}><FiSave /> Save settings</button>
        {msg && <span className="trx-savemsg"><FiCheckCircle /> {msg}</span>}
        {error && <span className="trx-error" style={{ margin: 0 }}>{error}</span>}
      </div>
    </div>
  );
}
